import type { HotspotModalProps } from '@integration-layer/components/Hotspot/HotspotModal.props'

/**
 * Manages the state and interactions for a hotspot modal component.
 *
 * @function useHotspotModal
 * @returns {Object} An object containing hotspot modal state and state setter method.
 *
 * @description
 * This composable provides a reactive state management solution for hotspot modals.
 * It allows setting and accessing hotspot modal properties dynamically.
 *
 * @property {Ref<HotspotModalProps | undefined>} hotspotModalState - Reactive state of the hotspot modal.
 * @property {Function} setHotspotModalState - Method to update the hotspot modal state.
 *
 * @example
 * // Using the hotspot modal composable
 * const { hotspotModalState, setHotspotModalState } = useHotspotModal()
 *
 * // Set modal props
 * setHotspotModalState({ title: 'Product Details', content: '...' })
 *
 * // Access current modal state
 * console.log(hotspotModalState.value)
 */
export const useHotspotModal = () => {
  const hotspotModalState = useState<HotspotModalProps | undefined>(
    'hotspot-modal-props'
  )

  const setHotspotModalState = (props: HotspotModalProps | undefined) => {
    hotspotModalState.value = props
  }

  return {
    hotspotModalState,
    setHotspotModalState,
  }
}
